"use client";

// /experiment — Refer & Earn banner family (green).
// 10 distinct compositional directions paired 1:1 with the offer banner so
// changes between Welcome and Refer feel like the same brand making
// different moves on different surfaces.

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PicksPIcon } from "@/components/ui/icons";
import { useExperimentVariant } from "@/lib/experiment/use-variant";
import { VariantPicker } from "@/components/experiment/offer-promo-banner";

interface Props {
  onInvite?: () => void;
  className?: string;
}

interface PublicProps extends Props {
  /** When set, render this variant (1-based) and ignore the experiment switcher. */
  forceVariant?: number;
}

const GREEN = "hsl(160 65% 38%)";
const GREEN_DARK = "hsl(160 65% 26%)";
const YELLOW = "#ffd45e";
const FONT: React.CSSProperties = {
  fontFamily: "var(--font-open-sauce), system-ui, sans-serif",
};

const STORAGE_KEY = "alexey:experiment-refer-banner";
const VARIANT_LABELS = [
  "Default · F-mark chip",
  "Diagonal slab",
  "Number hero — $20.",
  "Vertical 2-tone",
  "Yellow ribbon top",
  "F-mark watermark",
  "Chip cluster (3 stickers)",
  "Postcard frame",
  "Tilted composition",
  "Wordmark headline (Sushi-style)",
  "Monthly progress · friend slots",
  "Revenue share · up to 40%",
] as const;

/* ─── Shared primitives ────────────────────────────────────────────────── */

function Shell({
  children,
  className,
  bg = GREEN,
  darkBg = GREEN_DARK,
  onInvite,
  label = "Invite a friend, earn $20",
}: {
  children: React.ReactNode;
  className?: string;
  bg?: string;
  darkBg?: string;
  onInvite?: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onInvite}
      aria-label={label}
      className={cn(
        "group relative h-[136px] w-full overflow-hidden rounded-[14px] text-left transition-[filter] duration-200 hover:brightness-[1.06] focus:outline-none",
        className,
      )}
      style={{ background: bg, ...FONT }}
    >
      <DarkAccent darkBg={darkBg} />
      <Halftone />
      {children}
    </button>
  );
}

/* Halftone dot HUD — subtle white dot grid across the whole surface, soft
   overlay blend so it reads as printed paper texture, not loud pattern. */
function Halftone() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
        backgroundSize: "7px 7px",
        opacity: 0.22,
        mixBlendMode: "overlay",
      }}
    />
  );
}

/* Dark-accent wash — two stacked diagonal gradients ease the base into a
   near-black brand tone toward the lower-right. Top layer is black for
   weight; under-layer keeps a hint of the brand hue. No hard edge. */
function DarkAccent({ darkBg }: { darkBg: string }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        background: `
          linear-gradient(115deg, transparent 0%, transparent 30%, rgba(0,0,0,0.85) 100%),
          linear-gradient(115deg, transparent 0%, transparent 25%, ${darkBg} 100%)
        `,
        opacity: 0.9,
      }}
    />
  );
}

function Wordmark({ left = 16, top = 16, width = 94 }: { left?: number; top?: number; width?: number }) {
  return (
    <span className="absolute block" style={{ left, top, width, height: width * 0.1138 }}>
      <Image
        src="/getpicks-logo.svg"
        alt="GetPicks"
        fill
        sizes={`${width}px`}
        className="object-contain object-left brightness-0 invert"
        priority
      />
    </span>
  );
}

function FmarkChip({ size = 92, rotate = -10 }: { size?: number; rotate?: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: "white",
        borderRadius: size * 0.18,
        boxShadow: "0 6px 0 rgba(0,0,0,0.18), 0 0 0 4px white",
      }}
    >
      <PicksPIcon className="h-[40px] w-[54px]" style={{ color: GREEN }} />
    </div>
  );
}

function TwentyChip({
  size = 92,
  fontSize = 32,
  rotate = -8,
}: { size?: number; fontSize?: number; rotate?: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex flex-col items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: "white",
        borderRadius: size * 0.18,
        boxShadow: "0 6px 0 rgba(0,0,0,0.18), 0 0 0 4px white",
      }}
    >
      <span className="font-bold leading-none tracking-[-0.03em]" style={{ color: GREEN, fontSize }}>$20</span>
      <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.18em]" style={{ color: GREEN }}>Per friend</span>
    </div>
  );
}

function YellowFriendChip({ size = 48, rotate = 14 }: { size?: number; rotate?: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex flex-col items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: YELLOW,
        borderRadius: size * 0.2,
        boxShadow: "0 4px 0 rgba(0,0,0,0.18), 0 0 0 3px " + YELLOW,
      }}
    >
      <span className="font-bold leading-none tracking-[-0.03em] text-black" style={{ fontSize: size * 0.36 }}>+1</span>
      <span className="mt-0.5 text-[7px] font-bold uppercase tracking-[0.16em] text-black/75">Friend</span>
    </div>
  );
}

function CopyBlock({
  pillBg = "white",
  pillFg = GREEN,
  pr = 130,
  pl = 16,
  pt = 26,
  align = "left",
}: {
  pillBg?: string;
  pillFg?: string;
  pr?: number;
  pl?: number;
  pt?: number;
  align?: "left" | "right" | "center";
}) {
  const alignClass =
    align === "right" ? "items-end text-right" : align === "center" ? "items-center text-center" : "items-start";
  return (
    <div
      className={cn("relative z-10 flex h-full flex-col justify-center gap-1.5", alignClass)}
      style={{ paddingLeft: pl, paddingRight: pr, paddingTop: pt }}
    >
      <h3 className="text-[24px] font-bold leading-[1.05] tracking-tight text-white">
        Invite a friend.
        <br />
        Earn $20.
      </h3>
      <span
        aria-hidden
        className="inline-flex h-7 w-fit items-center rounded-full px-4 text-[12px] font-semibold tracking-tight"
        style={{ background: pillBg, color: pillFg }}
      >
        Invite
      </span>
    </div>
  );
}

/* ─── 10 variants ──────────────────────────────────────────────────────── */

function V1({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <Wordmark />
      <div className="absolute" style={{ right: 16, top: "50%", transform: "translateY(-50%)" }}>
        <FmarkChip />
      </div>
      <CopyBlock />
    </Shell>
  );
}

function V2({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: GREEN_DARK,
          clipPath: "polygon(60% 0, 100% 0, 100% 100%, 30% 100%)",
        }}
      />
      <Wordmark />
      <div className="absolute" style={{ right: 18, top: "50%", transform: "translateY(-50%)" }}>
        <FmarkChip rotate={-6} />
      </div>
      <CopyBlock />
    </Shell>
  );
}

function V3({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <Wordmark />
      {/* Number hero — scales with viewport. At 280-px banners (Telegram
          mini-app / inner-fold widths) the 76px hero would crush the
          headline, so we clamp it down hard at the bottom. */}
      <div
        className="absolute font-bold leading-none tracking-[-0.04em] text-white sm:text-[76px]"
        style={{
          right: 14,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: "clamp(40px, 13vw, 76px)",
        }}
      >
        $20<span style={{ color: YELLOW }}>.</span>
      </div>
      {/* Inline copy — same locked text as the shared CopyBlock; on phone
          widths (banner ≈ 217–348px) the headline + right-padding scale
          down so "Invite a friend." never collides with the $20. number.
          From sm: up the values match the original CopyBlock(pr=140) layout. */}
      <div
        className="relative z-10 flex h-full flex-col justify-center gap-1.5 pl-4 pt-[26px] sm:pr-[140px]"
        style={{ paddingRight: "clamp(96px, 36vw, 140px)" }}
      >
        <h3
          className="font-bold leading-[1.05] tracking-tight text-white sm:text-[24px]"
          style={{ fontSize: "clamp(15px, 5vw, 24px)" }}
        >
          Invite a friend.
          <br />
          Earn $20.
        </h3>
        <span
          aria-hidden
          className="inline-flex h-7 w-fit items-center rounded-full bg-white px-4 text-[12px] font-semibold tracking-tight"
          style={{ color: GREEN }}
        >
          Invite
        </span>
      </div>
    </Shell>
  );
}

function V4({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0"
        style={{ left: "60%", right: 0, background: GREEN_DARK }}
      />
      <Wordmark />
      <div className="absolute" style={{ right: 24, top: "50%", transform: "translateY(-50%)" }}>
        <FmarkChip rotate={0} />
      </div>
      <CopyBlock pr={170} />
    </Shell>
  );
}

function V5({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 flex h-[22px] items-center px-4 text-[9px] font-bold uppercase tracking-[0.28em] text-black"
        style={{ background: YELLOW }}
      >
        Refer &amp; Earn · Per friend
      </div>
      <Wordmark top={32} />
      <div className="absolute" style={{ right: 16, top: 36 }}>
        <FmarkChip size={84} rotate={-6} />
      </div>
      <div
        className="relative z-10 flex h-full flex-col justify-end gap-1.5 pl-4 pr-[120px] pb-3 pt-[58px]"
      >
        <h3 className="text-[20px] font-bold leading-[1.05] tracking-tight text-white">
          Invite a friend. Earn $20.
        </h3>
        <span
          aria-hidden
          className="inline-flex h-7 w-fit items-center rounded-full bg-white px-4 text-[12px] font-semibold tracking-tight"
          style={{ color: GREEN }}
        >
          Invite
        </span>
      </div>
    </Shell>
  );
}

function V6({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{ right: -18, top: "50%", transform: "translateY(-50%)", opacity: 0.18 }}
      >
        <PicksPIcon className="h-[160px] w-[210px] text-white" />
      </span>
      <Wordmark />
      <div className="absolute" style={{ right: 18, top: "50%", transform: "translateY(-50%)" }}>
        <TwentyChip size={76} fontSize={26} rotate={-6} />
      </div>
      <CopyBlock pr={110} />
    </Shell>
  );
}

function V7({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <Wordmark />
      <div className="absolute" style={{ right: 6, top: 6 }}>
        <YellowFriendChip size={42} rotate={-12} />
      </div>
      <div className="absolute" style={{ right: 54, top: 16 }}>
        <FmarkChip size={48} rotate={14} />
      </div>
      <div className="absolute" style={{ right: 8, top: 50 }}>
        <TwentyChip size={84} fontSize={28} rotate={-8} />
      </div>
      <CopyBlock pr={120} />
    </Shell>
  );
}

function V8({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-2 rounded-[10px] border border-dashed border-white/55"
      />
      <Wordmark left={20} top={20} width={82} />
      <div className="absolute" style={{ right: 22, top: "50%", transform: "translateY(-50%)" }}>
        <FmarkChip size={84} rotate={-6} />
      </div>
      <div className="relative z-10 flex h-full flex-col justify-center gap-1.5 pl-5 pr-[120px] pt-[28px]">
        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/85">
          Refer &amp; Earn
        </div>
        <h3 className="text-[20px] font-bold leading-[1.05] tracking-tight text-white">
          Invite a friend. Earn $20.
        </h3>
        <span
          aria-hidden
          className="inline-flex h-7 w-fit items-center rounded-full bg-white px-4 text-[12px] font-semibold tracking-tight"
          style={{ color: GREEN }}
        >
          Invite
        </span>
      </div>
    </Shell>
  );
}

function V9({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{ right: -28, top: -22, opacity: 0.12 }}
      >
        <PicksPIcon className="h-[200px] w-[260px] text-white" />
      </span>
      <div style={{ transform: "rotate(-1.5deg)", transformOrigin: "left top" }}>
        <Wordmark />
        <CopyBlock pr={128} />
      </div>
      <div
        className="absolute"
        style={{ right: 14, top: "50%", transform: "translateY(-50%) rotate(4deg)" }}
      >
        <FmarkChip rotate={0} />
      </div>
      <div className="absolute" style={{ right: 92, top: 12 }}>
        <YellowFriendChip rotate={-12} />
      </div>
    </Shell>
  );
}

function V10({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className}>
      <div
        className="absolute font-bold leading-[0.92] tracking-[-0.025em] text-white"
        style={{ left: 18, top: 16, fontSize: 36 }}
      >
        Picks.
      </div>
      <div className="absolute" style={{ left: 18, top: 60 }}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/85">
          Refer &amp; Earn
        </div>
        <div className="mt-0.5 text-[18px] font-bold tracking-tight text-white">
          Invite a friend. Earn $20.
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span
            aria-hidden
            className="inline-flex h-6 items-center rounded-full bg-white px-3 text-[11px] font-semibold tracking-tight"
            style={{ color: GREEN }}
          >
            Invite
          </span>
        </div>
      </div>
      <div className="absolute" style={{ right: 16, top: "50%", transform: "translateY(-50%)" }}>
        <FmarkChip rotate={-8} />
      </div>
      <div className="absolute" style={{ right: 92, top: 14 }}>
        <YellowFriendChip />
      </div>
    </Shell>
  );
}

/* Monthly progress: friend slots fill as invites land, the hero number is
   what is still on the table this month. Mock state, 3 of 10 in. */
const MONTH_MAX = 10;
const PER_FRIEND = 20;
const MOCK_FRIENDS = 3;
const MOCK_RESET_DAYS = 22;
const SLOT_AVATARS = [
  "/avatars/c/dana_k.svg",
  "/avatars/c/marcus.svg",
  "/avatars/c/lexplays.svg",
  "/avatars/c/nightowl.svg",
  "/avatars/c/courtside.svg",
  "/avatars/c/hotread.svg",
  "/avatars/c/jrod.svg",
  "/avatars/c/drezy.svg",
  "/avatars/c/chalk.svg",
  "/avatars/c/longshot.svg",
];

function V11({ onInvite, className }: Props) {
  const n: number = MOCK_FRIENDS;
  const maxed = n >= MONTH_MAX;
  const earned = n * PER_FRIEND;
  const left = (MONTH_MAX - n) * PER_FRIEND;
  const label = maxed ? "Earned this month" : n === 0 ? "Up to this month" : "Left to earn";
  const [headTop, headBottom] = maxed
    ? ["You maxed out", "this month."]
    : [n === MONTH_MAX - 1 ? "One more friend." : "Invite a friend.", `Earn $${PER_FRIEND}.`];
  // Sizes follow the banner's own width (container units), so the same
  // layout holds from the 260px phone card to the 620px desktop slot.
  const wide = "hidden [@container(min-width:560px)]:inline";
  return (
    <Shell onInvite={onInvite} className={className}>
      <Wordmark top={14} />
      <div className="absolute inset-0 z-10 [container-type:inline-size]">
        <div className="flex h-full flex-col px-4 pb-3.5 pt-3.5">
          <div className="flex min-h-0 flex-1 items-start justify-between gap-3">
            <h3
              className="min-w-0 pt-[18px] font-bold leading-[1.02] tracking-tight text-white"
              style={{ fontSize: "clamp(15px, 4.6cqw, 24px)" }}
            >
              {headTop}
              <br />
              {headBottom}
            </h3>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">{label}</span>
              <span
                className="font-bold leading-[0.85] tracking-[-0.05em] text-white"
                style={{ fontSize: "clamp(36px, 10.5cqw, 64px)" }}
              >
                ${maxed ? earned : left}
                <span style={{ color: YELLOW }}>.</span>
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex shrink-0 gap-[3px]">
                {SLOT_AVATARS.map((src, i) => {
                  const size = { width: "clamp(12px, 3.2cqw, 20px)", height: "clamp(12px, 3.2cqw, 20px)" };
                  return i < n ? (
                    <span
                      key={i}
                      className="relative overflow-hidden rounded-full border-2 border-white"
                      style={{ ...size, background: GREEN_DARK }}
                    >
                      <Image src={src} alt="" fill sizes="20px" className="object-cover" />
                    </span>
                  ) : (
                    <span
                      key={i}
                      className="rounded-full border border-dashed border-white/45 bg-white/[0.08]"
                      style={size}
                    />
                  );
                })}
              </div>
              <span className={cn(wide, "whitespace-nowrap text-[12px] font-semibold text-white")}>
                {n} of {MONTH_MAX}
                {n > 0 && <span className="font-normal text-white/75"> · ${earned} earned</span>}
              </span>
            </div>
            <span className="flex shrink-0 items-center gap-2.5">
              <span className={cn(wide, "text-[11px] text-white/80")}>Resets in {MOCK_RESET_DAYS} days</span>
              <span
                aria-hidden
                className="hidden h-7 items-center rounded-full px-4 text-[12px] font-semibold tracking-tight [@container(min-width:340px)]:inline-flex"
                style={maxed ? { background: "rgba(0,0,0,0.3)", color: "white" } : { background: "white", color: GREEN }}
              >
                {maxed ? "Partner stats" : n === 0 ? "Invite" : "Invite more"}
              </span>
            </span>
          </div>
        </div>
      </div>
    </Shell>
  );
}

/* Revenue share. Copy column and figure use the deposit banner's (offer V13)
   exact boxes, so the headlines and pills of the two sit on one line. */
function V12({ onInvite, className }: Props) {
  return (
    <Shell onInvite={onInvite} className={className} label="Invite friends, earn up to 40% revenue share">
      <Wordmark top={14} />
      <div
        className="pointer-events-none absolute z-10 flex flex-col items-center"
        style={{ right: 14, top: "50%", transform: "translateY(calc(-50% + 11px))" }}
      >
        <span
          className="block whitespace-nowrap font-bold leading-none tracking-[-0.04em] text-white sm:text-[62px]"
          style={{ fontSize: "clamp(36px, 12vw, 62px)" }}
        >
          40<span style={{ color: YELLOW }}>%</span>
        </span>
        <span className="mt-1 whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.18em] text-white/70">
          Revenue share · up to
        </span>
      </div>
      <div
        className="relative z-10 flex h-full flex-col justify-center gap-1.5 pl-4 pt-[26px] sm:pr-[150px]"
        style={{ paddingRight: "clamp(104px, 38vw, 150px)" }}
      >
        <h3
          className="font-bold leading-[1.05] tracking-tight text-white sm:text-[24px]"
          style={{ fontSize: "clamp(15px, 5vw, 24px)" }}
        >
          Invite friends.
          <br />
          Earn on every lineup.
        </h3>
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="inline-flex h-7 w-fit shrink-0 items-center whitespace-nowrap rounded-full bg-white px-4 text-[12px] font-semibold tracking-tight"
            style={{ color: GREEN }}
          >
            Invite
          </span>
          <span className="hidden whitespace-nowrap text-[12px] text-white/75 sm:inline">
            Terms apply
          </span>
        </div>
      </div>
    </Shell>
  );
}

const VARIANTS = [V1, V2, V3, V4, V5, V6, V7, V8, V9, V10, V11, V12];

/* ─── Public components ────────────────────────────────────────────────── */

export function ReferEarnBannerExperiment({ onInvite, className, forceVariant }: PublicProps) {
  const [hookVariant] = useExperimentVariant(STORAGE_KEY, VARIANTS.length);
  const variant = forceVariant ?? hookVariant;
  const Active = VARIANTS[variant - 1] ?? V1;
  return <Active onInvite={onInvite} className={className} />;
}

export function ReferEarnBannerSwitcher({ className }: { className?: string }) {
  const [variant, setVariant] = useExperimentVariant(STORAGE_KEY, VARIANTS.length);
  return (
    <VariantPicker
      label="Refer"
      accent="hsl(160 65% 55%)"
      variant={variant}
      labels={VARIANT_LABELS}
      onPick={setVariant}
      className={className}
    />
  );
}
